<template>
  <div class="data-reset-management">
    <el-card shadow="never">
      <div slot="header"><strong>数据一键清理</strong></div>

      <el-alert
        title="危险操作：清理后不可恢复。请先选择应用范围，再勾选要清理的数据类型。"
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <el-form label-width="110px" class="reset-form">
        <el-form-item label="应用范围">
          <el-radio-group v-model="scopeMode" :disabled="submitting">
            <el-radio label="partial">部分应用</el-radio>
            <el-radio label="all">全部应用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="scopeMode === 'partial'" label="选择应用">
          <el-select
            v-model="selectedAppIds"
            multiple
            filterable
            clearable
            placeholder="请选择要清理的应用"
            :loading="appsLoading"
            :disabled="submitting"
            style="width: 100%; max-width: 560px;"
          >
            <el-option
              v-for="app in apps"
              :key="app.id"
              :value="app.id"
              :label="(app.name || '未命名') + '（' + (app.packageName || '未配置包名') + '）'"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="清理类型">
          <el-checkbox-group v-model="selectedTypes" :disabled="submitting">
            <el-checkbox label="balance">用户余额（coin_balance + total_earned 置 0）</el-checkbox>
            <el-checkbox label="coinFlow">金币明细（删除 general_coin_flow）</el-checkbox>
            <el-checkbox label="withdrawal">用户提现记录（删除 withdrawal_record + 清每日次数缓存）</el-checkbox>
            <el-checkbox label="adPool">叠包记录（暂存池归零 + 删结算明细 + 清缓存）</el-checkbox>
          </el-checkbox-group>
        </el-form-item>

        <el-form-item>
          <el-button type="danger" icon="el-icon-delete" :loading="submitting" @click="confirmReset">
            执行清理
          </el-button>
          <el-button :disabled="submitting" @click="resetForm">重置选项</el-button>
        </el-form-item>
      </el-form>

      <el-card v-if="lastResult.length" shadow="never" class="result-card">
        <div slot="header"><strong>最近一次执行结果</strong></div>
        <el-timeline>
          <el-timeline-item
            v-for="(item, idx) in lastResult"
            :key="idx"
            :type="item.ok ? 'success' : 'danger'"
            :timestamp="item.time"
          >
            <div><strong>{{ item.label }}</strong> — {{ item.message }}</div>
          </el-timeline-item>
        </el-timeline>
      </el-card>
    </el-card>
  </div>
</template>

<script>
import { get, post, getResponseMessage } from '@/utils/http'

const TYPE_META = {
  balance: {
    label: '用户余额',
    path: '/admin/data-reset/balance',
    summarize(data) {
      return `余额 ${data.balanceRows != null ? data.balanceRows : 0} 行`
    }
  },
  coinFlow: {
    label: '金币明细',
    path: '/admin/data-reset/coin-flow',
    summarize(data) {
      return `明细 ${data.coinFlowRows != null ? data.coinFlowRows : 0} 条`
    }
  },
  withdrawal: {
    label: '用户提现记录',
    path: '/admin/data-reset/withdrawal',
    summarize(data) {
      return `提现 ${data.withdrawalRows != null ? data.withdrawalRows : 0} 条，缓存 ${data.redisKeys != null ? data.redisKeys : 0} 个`
    }
  },
  adPool: {
    label: '叠包记录',
    path: '/admin/data-reset/ad-pool',
    summarize(data) {
      return `暂存池 ${data.adPoolRows != null ? data.adPoolRows : 0} 行，结算 ${data.settlementRows != null ? data.settlementRows : 0} 条，缓存 ${data.redisKeys != null ? data.redisKeys : 0} 个`
    }
  }
}

export default {
  name: 'DataResetManagement',
  data() {
    return {
      apps: [],
      appsLoading: false,
      scopeMode: 'partial',
      selectedAppIds: [],
      selectedTypes: [],
      submitting: false,
      lastResult: []
    }
  },
  mounted() {
    this.loadApps()
  },
  methods: {
    async loadApps() {
      this.appsLoading = true
      try {
        const result = await get('/admin/app/list')
        this.apps = Array.isArray(result.data) ? result.data : []
      } catch (e) {
        this.$message.error(e.message || '应用列表加载失败')
      } finally {
        this.appsLoading = false
      }
    },
    resetForm() {
      this.scopeMode = 'partial'
      this.selectedAppIds = []
      this.selectedTypes = []
    },
    buildPayload() {
      if (this.scopeMode === 'all') {
        return { allApps: true }
      }
      return { allApps: false, appIds: this.selectedAppIds }
    },
    async confirmReset() {
      if (!this.selectedTypes.length) {
        this.$message.warning('请至少勾选一种清理类型')
        return
      }
      const payload = this.buildPayload()
      if (!payload.allApps && (!payload.appIds || !payload.appIds.length)) {
        this.$message.warning('请选择应用，或切换为全部应用')
        return
      }

      const scopeText = payload.allApps
        ? '全部应用'
        : `已选 ${payload.appIds.length} 个应用`
      const typeText = this.selectedTypes.map(t => TYPE_META[t].label).join('、')
      try {
        await this.$confirm(
          `即将对【${scopeText}】清理：${typeText}。数据不可恢复，是否继续？`,
          '二次确认',
          { type: 'warning', confirmButtonText: '确定清理', cancelButtonText: '取消' }
        )
      } catch (_) {
        return
      }

      this.submitting = true
      const results = []
      const now = new Date().toLocaleString()
      try {
        for (const type of this.selectedTypes) {
          const meta = TYPE_META[type]
          try {
            const result = await post(meta.path, payload)
            const data = result.data || {}
            results.push({
              ok: true,
              label: meta.label,
              time: now,
              message: meta.summarize(data)
            })
          } catch (e) {
            results.push({
              ok: false,
              label: meta.label,
              time: now,
              message: e.message || getResponseMessage(null, '清理失败')
            })
          }
        }
        this.lastResult = results
        const failed = results.filter(r => !r.ok)
        if (failed.length === 0) {
          this.$message.success('清理完成')
        } else if (failed.length === results.length) {
          this.$message.error('清理全部失败')
        } else {
          this.$message.warning(`部分成功，失败 ${failed.length} 项`)
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.reset-form {
  max-width: 720px;
}
.result-card {
  margin-top: 24px;
}
.el-checkbox {
  display: block;
  margin-left: 0 !important;
  margin-bottom: 10px;
}
</style>
